import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { useCreateDialogue } from "src/reusable/Chat/ChatStreaming/services/api";
import useAssistantNavigation from "src/views/assistant/hooks/useAssistantNavigation";
import type { GlobalSearchResult } from "../types/globalSearch.types";

/**
 * Hook to start a chat with search results
 * Single result: navigates to /assistant with preload state (same as existing buttons)
 * Multiple results: creates dialogue and navigates to chat
 */
export const useSearchResultChat = () => {
  const history = useHistory();
  const { navigateToChat } = useAssistantNavigation();
  const { mutateAsync: createDialogue } = useCreateDialogue();

  const {
    setDatasets,
    setSourceDataRecordIds,
    addUploadedFile,
    clearUploadedFiles,
    config: chatConfig,
  } = useChatStore();

  /**
   * Chat with a dataset - creates dialogue and auto-starts chat
   */
  const chatWithDataset = useCallback(
    async (result: GlobalSearchResult, searchQuery?: string) => {
      if (!result.metadata.datasetId) return;

      const userMessage = searchQuery
        ? `Tell me more about "${searchQuery}"`
        : undefined;

      try {
        // Create dialogue
        const dialogueId = await createDialogue({
          conversation: {
            model_id: chatConfig?.modelId ?? null,
            model_version_number: chatConfig?.modelVersionNum ?? null,
            search_datasets: [result.metadata.datasetId],
            n_search_results: chatConfig?.topSearchResults || 5,
            model_inference_data: {
              infer_data: JSON.stringify([]),
              stream: true,
              streaming_response_required: true,
              text_gen_model_params: chatConfig?.chatControlMetadata || {},
            },
            tool_calling: true,
            chat_config_id: chatConfig?.chatConfigId,
          },
        });

        // Set dataset in store (no documents)
        setDatasets([
          {
            id: result.metadata.datasetId,
            name: result.title,
            description: result.description || "",
          },
        ]);
        setSourceDataRecordIds([]);

        // Navigate to chat
        navigateToChat({
          dialogueId,
          options: {
            userMessage,
          },
        });
      } catch (error) {
        console.error("Failed to create dialogue:", error);
        // Fallback: navigate to splash
        history.push({
          pathname: "/assistant",
          state: {
            preloadDataset: {
              id: result.metadata.datasetId,
              name: result.title,
              description: result.description || "",
            },
          },
        });
      }
    },
    [
      createDialogue,
      chatConfig,
      setDatasets,
      setSourceDataRecordIds,
      navigateToChat,
      history,
    ],
  );

  /**
   * Chat with a document - creates dialogue and auto-starts chat
   */
  const chatWithDocument = useCallback(
    async (result: GlobalSearchResult, searchQuery?: string) => {
      const sdrId = result.metadata.documentId;
      const datasetId = result.metadata.datasetId;
      const fileName = result.metadata.documentName || result.title;

      if (!sdrId || !datasetId) return;

      const userMessage = searchQuery
        ? `Tell me more about "${searchQuery}"`
        : undefined;

      const uploadedFile = {
        id: `sdr-${sdrId}`,
        file: {
          name: fileName,
          size: 0,
          type: "application/pdf",
        } as File,
        datasetId: datasetId,
        sourceDataRecordId: sdrId,
        uploadStatus: "uploaded" as const,
        uploadProgress: 100,
        processingStatus: "completed" as const,
        processingProgress: 100,
      };

      try {
        // Create dialogue (no datasets - document search uses sourceDataRecordIds)
        const dialogueId = await createDialogue({
          conversation: {
            model_id: chatConfig?.modelId ?? null,
            model_version_number: chatConfig?.modelVersionNum ?? null,
            search_datasets: [],
            n_search_results: chatConfig?.topSearchResults || 5,
            model_inference_data: {
              infer_data: JSON.stringify([]),
              stream: true,
              streaming_response_required: true,
              text_gen_model_params: chatConfig?.chatControlMetadata || {},
            },
            tool_calling: true,
            chat_config_id: chatConfig?.chatConfigId,
          },
        });

        // Set document in store (NO datasets - document only)
        setDatasets([]);
        setSourceDataRecordIds([sdrId]);
        addUploadedFile(uploadedFile);

        // Navigate to chat with explicit empty datasets
        navigateToChat({
          dialogueId,
          options: {
            config: chatConfig
              ? { ...chatConfig, datasets: [] }
              : ({ datasets: [] } as any),
            userMessage,
            uploadedFiles: [uploadedFile],
            sourceDataRecordIds: [sdrId],
          },
        });
      } catch (error) {
        console.error("Failed to create dialogue:", error);
        // Fallback: navigate to splash
        history.push({
          pathname: "/assistant",
          state: {
            preloadDocument: {
              sdrId: sdrId,
              fileName: fileName,
              datasetId: datasetId,
            },
          },
        });
      }
    },
    [
      createDialogue,
      chatConfig,
      setDatasets,
      setSourceDataRecordIds,
      addUploadedFile,
      navigateToChat,
      history,
    ],
  );

  /**
   * Chat with a single search result (auto-detects type)
   */
  const chatWithResult = useCallback(
    async (result: GlobalSearchResult, searchQuery?: string) => {
      if (result.type === "dataset") {
        await chatWithDataset(result, searchQuery);
      } else {
        await chatWithDocument(result, searchQuery);
      }
    },
    [chatWithDataset, chatWithDocument],
  );

  /**
   * Chat with multiple search results
   * Creates a dialogue and navigates to chat with auto-start message
   */
  const chatWithResults = useCallback(
    async (results: GlobalSearchResult[], searchQuery?: string) => {
      // Clear any existing state to start fresh
      clearUploadedFiles();

      // Collect all source data record IDs from document results
      const documentResults = results.filter(
        (r) => r.type === "chunk" && r.metadata.documentId,
      );
      const sdrIds = documentResults.map(
        (r) => r.metadata.documentId as number,
      );

      // Collect all unique dataset IDs
      const datasetIds = [
        ...new Set(results.map((r) => r.metadata.datasetId).filter(Boolean)),
      ] as number[];

      // Determine if we're chatting with documents or datasets
      const isDocumentChat = documentResults.length > 0;
      const isDatasetChat = results.some((r) => r.type === "dataset");

      // Build datasets array for dataset chat
      const datasets = isDatasetChat
        ? datasetIds.map((id) => {
            const match = results.find((r) => r.metadata.datasetId === id);
            return {
              id,
              name: match?.metadata.datasetName || `Dataset ${id}`,
              description: "",
            };
          })
        : [];

      // Set store state based on chat type
      if (isDatasetChat && !isDocumentChat) {
        // Chatting with datasets only
        setDatasets(datasets);
        setSourceDataRecordIds([]);
      } else if (isDocumentChat) {
        // Chatting with documents only
        setDatasets([]);
        setSourceDataRecordIds(sdrIds);
      }

      // Build uploaded files for UI display (only for document chat)
      const uploadedFiles = isDocumentChat
        ? documentResults
            .filter(
              (result) =>
                result.metadata.documentId && result.metadata.datasetId,
            )
            .map((result) => ({
              id: `sdr-${result.metadata.documentId}`,
              file: {
                name: result.metadata.documentName || result.title,
                size: 0,
                type: "application/pdf",
              } as File,
              datasetId: result.metadata.datasetId!,
              sourceDataRecordId: result.metadata.documentId!,
              uploadStatus: "uploaded" as const,
              uploadProgress: 100,
              processingStatus: "completed" as const,
              processingProgress: 100,
            }))
        : [];

      // Add each document to store for immediate UI display
      uploadedFiles.forEach((file) => addUploadedFile(file));

      const userMessage = searchQuery
        ? `Tell me more about "${searchQuery}"`
        : undefined;

      // Create dialogue and navigate to chat
      // For document chat, don't pass datasets - backend uses sourceDataRecordIds
      try {
        const dialogueId = await createDialogue({
          conversation: {
            model_id: chatConfig?.modelId ?? null,
            model_version_number: chatConfig?.modelVersionNum ?? null,
            search_datasets: isDocumentChat ? [] : datasetIds,
            n_search_results: chatConfig?.topSearchResults || 5,
            model_inference_data: {
              infer_data: JSON.stringify([]),
              stream: true,
              streaming_response_required: true,
              text_gen_model_params: chatConfig?.chatControlMetadata || {},
            },
            tool_calling: true,
            chat_config_id: chatConfig?.chatConfigId,
          },
        });

        // Navigate with appropriate options based on chat type
        if (isDocumentChat) {
          navigateToChat({
            dialogueId,
            options: {
              // Explicitly clear datasets - we're chatting with documents only
              config: chatConfig
                ? { ...chatConfig, datasets: [] }
                : ({ datasets: [] } as any),
              userMessage,
              uploadedFiles,
              sourceDataRecordIds: sdrIds,
            },
          });
        } else {
          navigateToChat({
            dialogueId,
            options: {
              config: chatConfig
                ? { ...chatConfig, datasets }
                : ({ datasets } as any),
              userMessage,
            },
          });
        }
      } catch (error) {
        console.error(
          "Failed to create dialogue for chat with results:",
          error,
        );
        // Fallback: navigate to splash
        history.push({
          pathname: "/assistant",
          state: {
            preloadSearchResults: {
              datasetIds: isDatasetChat ? datasetIds : [],
              documentIds: isDocumentChat ? sdrIds : [],
              resultCount: results.length,
            },
          },
        });
      }
    },
    [
      setDatasets,
      setSourceDataRecordIds,
      addUploadedFile,
      clearUploadedFiles,
      createDialogue,
      navigateToChat,
      chatConfig,
      history,
    ],
  );

  return {
    chatWithResult,
    chatWithDataset,
    chatWithDocument,
    chatWithResults,
  };
};
